import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('listovna_token'));
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(() => {
        const savedSettings = localStorage.getItem('listovna_settings');
        if (savedSettings) {
            try {
                return JSON.parse(savedSettings);
            } catch {
                return {
                    font_family: 'Cormorant Garamond',
                    font_size: 18,
                    line_height: 1.8,
                    dark_mode: false
                };
            }
        }
        return {
            font_family: 'Cormorant Garamond',
            font_size: 18,
            line_height: 1.8,
            dark_mode: false
        };
    });

    const getAuthHeaders = useCallback(() => {
        if (!token) return {};
        return { Authorization: `Bearer ${token}` };
    }, [token]);

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: getAuthHeaders()
            });
            setUser(response.data);
            
            // Fetch settings
            const settingsRes = await axios.get(`${API}/user/settings`, {
                headers: getAuthHeaders()
            });
            setSettings(settingsRes.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('listovna_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token, getAuthHeaders]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    useEffect(() => {
        // Apply dark mode
        if (settings.dark_mode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [settings.dark_mode]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('listovna_token', newToken);
        setToken(newToken);
        setUser(userData);
        
        // Fetch settings after login
        try {
            const settingsRes = await axios.get(`${API}/user/settings`, {
                headers: { Authorization: `Bearer ${newToken}` }
            });
            setSettings(settingsRes.data);
        } catch (e) {
            console.error('Failed to fetch settings:', e);
        }
        
        return userData;
    };

    const register = async (name, email, password) => {
        const response = await axios.post(`${API}/auth/register`, { name, email, password });
        return response.data;
    };

    const logout = () => {
        localStorage.removeItem('listovna_token');
        setToken(null);
        setUser(null);
        setSettings({
            font_family: 'Cormorant Garamond',
            font_size: 18,
            line_height: 1.8,
            dark_mode: false
        });
    };

    const updateSettings = async (newSettings) => {
        // Always update local state and localStorage
        setSettings(newSettings);
        localStorage.setItem('listovna_settings', JSON.stringify(newSettings));
        
        // If user is logged in, also sync to server
        if (user) {
            try {
                await axios.put(`${API}/user/settings`, newSettings, {
                    headers: getAuthHeaders()
                });
            } catch (error) {
                console.error('Failed to sync settings to server:', error);
            }
        }
        return newSettings;
    };

    const isSubscribed = () => {
        if (!user) return false;
        if (user.role === 'admin') return true;
        return user.subscription_status === 'active' || user.subscription_status === 'trialing';
    };

    const isAdmin = () => {
        return user?.role === 'admin';
    };

    const value = {
        user,
        token,
        loading,
        settings,
        login,
        register,
        logout,
        updateSettings,
        isSubscribed,
        isAdmin,
        getAuthHeaders
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
